import { useMemo } from 'react';
import { useFiltersStore } from '../stores/appFiltersStore';

export interface OrderFilters {
  platforms: string[];
  sections: string[];
  categories: string[];
  subCategories: string[];
}

export interface UseOrderFiltersConfig {
  includeRestaurants?: boolean;
  includePlatforms?: boolean;
  includeSections?: boolean;
  includeCategories?: boolean;
  includeSubCategories?: boolean;
}

export interface UseOrderFiltersReturn {
  filters: OrderFilters;
  setPlatformFilters?: (filters: string[]) => void;
  setSectionFilters?: (filters: string[]) => void;
  setCategoryFilters?: (filters: string[]) => void;
  setSubCategoryFilters?: (filters: string[]) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
}

/**
 * Hook for managing order history filters
 * Returns current filter state and setters for updating filters
 * @param config - Optional configuration to include specific filters
 */
export function useOrderFilters(config: UseOrderFiltersConfig = {
  includeRestaurants: false,
  includePlatforms: true,
  includeSections: false,
  includeCategories: true,
  includeSubCategories: true,
}): UseOrderFiltersReturn {
  const {
    includeRestaurants = false,
    includePlatforms = true,
    includeSections = false,
    includeCategories = true,
    includeSubCategories = true,
  } = config;

  const restaurantFilters = useFiltersStore(s => s.restaurantFilters);
  const platformFilters = useFiltersStore(s => s.platformFilters);
  const setPlatformFilters = useFiltersStore(s => s.setPlatformFilters);
  const sectionFilters = useFiltersStore(s => s.sectionFilters);
  const setSectionFilters = useFiltersStore(s => s.setSectionFilters);
  const categoryFilters = useFiltersStore(s => s.categoryFilters);
  const setCategoryFilters = useFiltersStore(s => s.setCategoryFilters);
  const subCategoryFilters = useFiltersStore(s => s.subCategoryFilters);
  const setSubCategoryFilters = useFiltersStore(s => s.setSubCategoryFilters);
  const resetOrderFilters = useFiltersStore(s => s.resetOrderFilters);

  const filters: OrderFilters = useMemo(() => ({
    platforms: includePlatforms ? platformFilters : [],
    sections: includeSections ? sectionFilters : [],
    categories: includeCategories ? categoryFilters : [],
    subCategories: includeSubCategories ? subCategoryFilters : [],
  }), [platformFilters, sectionFilters, categoryFilters, subCategoryFilters, includePlatforms, includeSections, includeCategories, includeSubCategories]);

  const hasActiveFilters = useMemo(() => {
    return (includeRestaurants && restaurantFilters.length > 0) ||
           (includePlatforms && platformFilters.length > 0) ||
           (includeSections && sectionFilters.length > 0) ||
           (includeCategories && categoryFilters.length > 0) ||
           (includeSubCategories && subCategoryFilters.length > 0);
  }, [restaurantFilters, platformFilters, sectionFilters, categoryFilters, subCategoryFilters, includeRestaurants, includePlatforms, includeSections, includeCategories, includeSubCategories]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (includeRestaurants && restaurantFilters.length > 0) count++;
    if (includePlatforms && platformFilters.length > 0) count++;
    if (includeSections && sectionFilters.length > 0) count++;
    if (includeCategories && categoryFilters.length > 0) count++;
    if (includeSubCategories && subCategoryFilters.length > 0) count++;
    return count;
  }, [restaurantFilters, platformFilters, sectionFilters, categoryFilters, subCategoryFilters, includeRestaurants, includePlatforms, includeSections, includeCategories, includeSubCategories]);

  return {
    filters,
    setPlatformFilters: includePlatforms ? setPlatformFilters : undefined,
    setSectionFilters: includeSections ? setSectionFilters : undefined,
    setCategoryFilters: includeCategories ? setCategoryFilters : undefined,
    setSubCategoryFilters: includeSubCategories ? setSubCategoryFilters : undefined,
    resetFilters: resetOrderFilters,
    hasActiveFilters,
    activeFilterCount,
  };
}
